import { useCallback, useRef } from 'react';
import type { ApolloCache, QueryOptions } from '@apollo/client';
import type {
  DeleteMembershipMutation,
  DeleteMembershipMutationResult,
  DeleteMembershipPayload,
  GetDirectMembershipsQuery,
  Membership,
  Resource,
  SupportedLocale,
} from '@frameio/graphql-client/generated';
import {
  GetDirectMemberships,
  useDeleteMembershipMutation,
} from '@frameio/graphql-client/generated';
import { intl } from '@frameio/i18n';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { toSupportedLocaleOrDefault } from '@frameio/shared/utils/toSupportedLocale';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import TEXT from './generated/TEXT/useDeleteMembership';

TEXT.DEFINE({
  /** Success toast message when member is removed from resource */
  success: (memberName: string, resourceName: string) =>
    `${memberName} removed from ${resourceName}`,

  /** Error toast message when member removal fails */
  error: (resourceName: string, errorMessage?: string) =>
    `Unable to remove from ${resourceName}${
      errorMessage ? `: ${errorMessage}` : ''
    }`,
});

type CacheUpdateResult = { hideUndoButton?: boolean } | undefined;

type CacheUpdateResolver = (value: CacheUpdateResult) => void;

export type DeleteMembershipArgs = {
  memberName: string;
  membershipId: Membership['id'];
  refetchQueries?: Array<string | QueryOptions>;
  successCta?: {
    label: string;
    callback: (membershipId: Membership['id']) => void;
  };
};

export type DeleteMembershipMutationArgs = {
  onBeginMutation?: (
    type: string,
    id: Membership['id']
  ) => Promise<void> | void;
  onCompleteMutation?: (
    id: Membership['id'],
    deletedMembership?: Membership
  ) => void;
  onUpdateCache?: (
    cache: ApolloCache<any>,
    result: DeleteMembershipMutation
  ) => Promise<CacheUpdateResult> | CacheUpdateResult | void;
  refetchQueries?: Array<string | QueryOptions>;
  resourceId: Resource['id'];
  resourceName: Resource['name'];
};

export const useDeleteMembership = ({
  onBeginMutation,
  onUpdateCache,
  resourceId,
  resourceName,
}: DeleteMembershipMutationArgs) => {
  const tryCatchOnSubmit = useToastTryCatchHandler({ duration: 4000 });
  const deletedMembershipIndex = useRef(0);

  const setDeletedMembershipIndex = useCallback(
    (
      deletedMembershipId: Membership['id'],
      memberships: GetDirectMembershipsQuery['directMemberships']
    ) => {
      memberships?.nodes.forEach(({ id }, index) => {
        if (id === deletedMembershipId) {
          deletedMembershipIndex.current = index;
        }
      });
    },
    []
  );

  const locale = toSupportedLocaleOrDefault(intl().locale);
  const accountId = useActiveAccountId();
  const [deleteMembershipMutation] = useDeleteMembershipMutation({
    awaitRefetchQueries: true,
  });

  const deleteMembership = useCallback(
    async ({
      memberName,
      membershipId,
      successCta,
      refetchQueries,
    }: DeleteMembershipArgs) => {
      await onBeginMutation?.('delete', membershipId);
      let cacheUpdateResult: CacheUpdateResult;

      const response = await new Promise((resolve) => {
        tryCatchOnSubmit<DeleteMembershipMutationResult>({
          attempt: async () => {
            let cacheUpdateResolve: CacheUpdateResolver;
            const cacheUpdatePromise = new Promise<CacheUpdateResult>(
              (resolveCacheUpdate) => {
                cacheUpdateResolve = resolveCacheUpdate;
              }
            );

            const result = await deleteMembershipMutation({
              variables: { locale, input: { id: membershipId } },
              refetchQueries,
              update: async (cache, { data }) => {
                const existingMemberships = accountId
                  ? readMembershipsFromCache(
                      cache,
                      accountId,
                      resourceId,
                      locale
                    )
                  : null;

                if (
                  accountId &&
                  existingMemberships &&
                  data?.deleteMembership?.membership
                ) {
                  const { membership } = data.deleteMembership;
                  const { directMemberships } = existingMemberships;
                  setDeletedMembershipIndex(membership.id, directMemberships);
                  updateDirectMembershipsCache({
                    accountId,
                    cache,
                    resourceId,
                    locale,
                    deletedMembershipId: membership.id,
                    existingMemberships,
                  });

                  const updateResult =
                    (await onUpdateCache?.(cache, data)) || undefined;
                  cacheUpdateResolve(updateResult);
                } else {
                  cacheUpdateResolve(undefined);
                }
              },
            });

            cacheUpdateResult = await cacheUpdatePromise;
            return result;
          },
          successMessage(success) {
            const membership = success?.data?.deleteMembership?.membership;
            resolve(membership);

            const name =
              memberName ||
              membership?.accountUser?.name ||
              membership?.accountUserGroup?.name;

            if (name) {
              return TEXT.success(name, resourceName) as string;
            }

            return '';
          },
          overwriteOptionsOnSuccess: () => {
            return successCta && !cacheUpdateResult?.hideUndoButton
              ? {
                  buttonLabel: successCta.label,
                  buttonCallback: () => successCta.callback(membershipId),
                }
              : {};
          },
          errorMessage(error) {
            resolve(error);
            return TEXT.error(resourceName, error) as string;
          },
        });
      });

      return response as Promise<DeleteMembershipPayload>;
    },
    [
      onBeginMutation,
      tryCatchOnSubmit,
      deleteMembershipMutation,
      accountId,
      locale,
      resourceId,
      setDeletedMembershipIndex,
      onUpdateCache,
      resourceName,
    ]
  );

  return { deleteMembership, deletedMembershipIndex };
};

const updateDirectMembershipsCache = ({
  accountId,
  cache,
  resourceId,
  locale,
  deletedMembershipId,
  existingMemberships,
}: {
  accountId: string;
  cache: ApolloCache<any>;
  resourceId: Resource['id'];
  locale: SupportedLocale;
  deletedMembershipId: Membership['id'];
  existingMemberships: GetDirectMembershipsQuery;
}) => {
  cache.writeQuery({
    query: GetDirectMemberships,
    variables: {
      accountId,
      locale,
      resourceId,
      page: {
        first: DEFAULT_INITIAL_PAGE_SIZE,
      },
    },
    data: {
      ...existingMemberships,
      directMemberships: {
        ...existingMemberships.directMemberships,
        nodes: existingMemberships.directMemberships.nodes.filter(
          ({ id }) => id !== deletedMembershipId
        ),
        totalCount: existingMemberships.directMemberships.totalCount - 1,
      },
      unfilteredDirectMembershipsTotalCount: {
        ...existingMemberships.unfilteredDirectMembershipsTotalCount,
        totalCount:
          existingMemberships.unfilteredDirectMembershipsTotalCount.totalCount -
          1,
      },
    },
  });
};

const readMembershipsFromCache = (
  cache: ApolloCache<any>,
  accountId: string,
  resourceId: string,
  locale: SupportedLocale
) => {
  return cache.readQuery({
    query: GetDirectMemberships,
    variables: {
      accountId,
      locale,
      resourceId,
      page: {
        first: DEFAULT_INITIAL_PAGE_SIZE,
      },
    },
  });
};
